// "/" command palette (7 Sep 2026, branch command-palette-2026-09-07).
//
// Opens on "/" (when focus is not in a field), Ctrl/Cmd+K, the Header "Search" pill,
// or openCommandPalette() from lib/commandRegistry. Mount it ONCE, in MainLayout.
//
// Sources (see lib/commandRegistry.js for how each one self-populates):
//   Recent    localStorage, last 8 things opened from here
//   Actions   registerCommand()/useRegisterCommands() + DEFAULT_ACTIONS
//   Pages     Sidebar.js navItems (role-filtered like the sidebar)
//   Settings  SettingsPage sections -> /ats/settings?section=<id>
//   Features  GET /api/search/catalogue - every backend route family
//   Live      GET /api/search/global?q= - candidates, resumes, requirements, clients, jobs
//
// Built on the vendored ui/dialog (Radix) with its own list + keyboard handling, so it
// does not depend on the cmdk package being present in node_modules.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  Search, CornerDownLeft, Clock, Zap, LayoutGrid, Settings, Boxes, Users, FileText,
  ClipboardList, Handshake, Briefcase, Loader2, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import {
  PALETTE_EVENT, DEFAULT_ACTIONS, SETTINGS_SECTIONS, getCommands, subscribeCommands,
  getNavItems, filterEntries, readRecent, pushRecent, clearRecent,
} from '../lib/commandRegistry';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DEBOUNCE_MS = 220;
const MIN_LIVE = 2;

const GROUP_ICON = {
  recent: Clock, actions: Zap, pages: LayoutGrid, settings: Settings, features: Boxes,
  candidates: Users, resumes: FileText, requirements: ClipboardList, clients: Handshake, jobs: Briefcase,
};

let catalogueCache = null;      // { features: [...] } - fetched once per page load
let cataloguePromise = null;

function authHeaders() {
  const token = (() => { try { return localStorage.getItem('ats_token'); } catch (e) { return null; } })();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchCatalogue() {
  if (catalogueCache) return catalogueCache;
  if (!cataloguePromise) {
    cataloguePromise = fetch(`${API}/api/search/catalogue`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { features: [], routes: [] }))
      .then((j) => { catalogueCache = j; return j; })
      .catch(() => ({ features: [], routes: [] }))
      .finally(() => { cataloguePromise = null; });
  }
  return cataloguePromise;
}

function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || !!el.isContentEditable;
}

function Snippet({ s }) {
  if (!s) return null;
  return (
    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
      {s.before}<mark className="rounded bg-amber-200 px-0.5 text-slate-900 dark:bg-amber-500/60 dark:text-white">{s.hit}</mark>{s.after}
    </span>
  );
}

const CommandPalette = () => {
  const navigate = useNavigate();
  const { user, canAccess } = useAuth();
  const hasAgencyPermission = user?.role === 'admin' || user?.role === 'super_admin' || !!user?.permissions?.agency_workspace;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [live, setLive] = useState([]);          // groups from /api/search/global
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);  // from /api/search/catalogue
  const [registryTick, setRegistryTick] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reqRef = useRef(0);

  // ---- open/close plumbing ------------------------------------------------------------------
  const openPalette = useCallback((initial) => {
    setQ(initial || '');
    setActive(0);
    setRecent(readRecent());
    setOpen(true);
  }, []);

  useEffect(() => {
    const onEvent = (e) => {
      const d = (e && e.detail) || {};
      if (d.open === false) setOpen(false);
      else openPalette(d.query);
    };
    window.addEventListener(PALETTE_EVENT, onEvent);
    return () => window.removeEventListener(PALETTE_EVENT, onEvent);
  }, [openPalette]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => { if (!v) { setQ(''); setActive(0); setRecent(readRecent()); } return !v; });
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isTypingTarget(e.target)) return;
        if (document.querySelector('[role="dialog"][data-state="open"]')) return; // another dialog is up
        e.preventDefault();
        openPalette('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette]);

  useEffect(() => subscribeCommands(() => setRegistryTick((t) => t + 1)), []);

  useEffect(() => {
    if (!open) return;
    fetchCatalogue().then((c) => setFeatures((c && c.features) || []));
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  // ---- live search (debounced) ---------------------------------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < MIN_LIVE) { setLive([]); setLoading(false); return undefined; }
    setLoading(true);
    const id = ++reqRef.current;
    const t = setTimeout(() => {
      fetch(`${API}/api/search/global?q=${encodeURIComponent(term)}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : { groups: [] }))
        .then((j) => { if (reqRef.current === id) setLive(j.groups || []); })
        .catch(() => { if (reqRef.current === id) setLive([]); })
        .finally(() => { if (reqRef.current === id) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, open]);

  // ---- assemble groups -----------------------------------------------------------------------
  const roleOk = useCallback((item) => {
    if (item.agencyOnly && !hasAgencyPermission) return false;
    if (item.minRole && !canAccess(item.minRole)) return false;
    return true;
  }, [canAccess, hasAgencyPermission]);

  const groups = useMemo(() => {
    const term = q.trim();
    const out = [];

    if (!term && recent.length) {
      out.push({ key: 'recent', label: 'Recent', items: recent.map((r) => ({ ...r, group: r.group || 'recent', kind: 'recent' })) });
    }

    const actions = [...DEFAULT_ACTIONS, ...getCommands()].filter(roleOk);
    const actionHits = filterEntries(actions, term, term ? 8 : 6);
    if (actionHits.length) {
      out.push({ key: 'actions', label: 'Actions', items: actionHits.map((a) => ({ ...a, id: `action:${a.id}`, subtitle: a.section !== 'Actions' ? a.section : null, kind: 'action' })) });
    }

    const pages = getNavItems().filter(roleOk).map((n) => ({ id: `page:${n.to}`, label: n.label, subtitle: n.section, to: n.to, keywords: n.to.replace('/ats/', '').replace(/[/-]/g, ' ') }));
    const pageHits = filterEntries(pages, term, term ? 6 : 8);
    if (pageHits.length) out.push({ key: 'pages', label: 'Pages', items: pageHits.map((p) => ({ ...p, kind: 'page' })) });

    if (term && canAccess('admin')) {
      const settings = SETTINGS_SECTIONS.map((s) => ({ id: `settings:${s.id}`, label: s.label, subtitle: `Settings · ${s.category}`, to: `/ats/settings?section=${s.id}`, keywords: `${s.keywords} settings ${s.id}` }));
      const hits = filterEntries(settings, term, 6);
      if (hits.length) out.push({ key: 'settings', label: 'Settings', items: hits.map((s) => ({ ...s, kind: 'settings' })) });
    }

    if (term && features.length) {
      const navByFamily = new Map(getNavItems().map((n) => [n.to.replace('/ats/', '').split('/')[0], n.to]));
      const feats = features.map((f) => {
        const navTo = navByFamily.get(f.slug) || navByFamily.get(f.slug.replace(/_/g, '-')) || null;
        return {
          id: `feature:${f.slug}`, label: f.label,
          subtitle: `${f.endpoints} endpoint${f.endpoints === 1 ? '' : 's'}${f.tags && f.tags.length ? ' · ' + f.tags.join(', ') : ''}${navTo ? '' : ' · API only'}`,
          to: navTo || `/ats/help?context=${encodeURIComponent('/api/' + f.slug)}`,
          keywords: `${(f.tags || []).join(' ')} ${(f.paths || []).join(' ')} api`,
        };
      });
      const hits = filterEntries(feats, term, 5);
      if (hits.length) out.push({ key: 'features', label: 'Features (backend)', items: hits.map((f) => ({ ...f, kind: 'feature' })) });
    }

    if (term) {
      live.forEach((g) => {
        out.push({
          key: g.key, label: g.label, more: g.more,
          items: (g.hits || []).map((h) => ({ ...h, id: `${g.key}:${h.id}`, label: h.title || h.label || '(untitled)', kind: 'live', group: g.key })),
        });
      });
    }
    return out;
  }, [q, recent, roleOk, live, features, canAccess, registryTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => { setActive(0); }, [q, groups.length]);

  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(`[data-index="${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // ---- selection -----------------------------------------------------------------------------
  const select = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQ('');
    pushRecent(item);
    setRecent(readRecent());
    if (item.to) navigate(item.to);
    else if (typeof item.run === 'function') { try { item.run(); } catch (e) { console.error('palette action failed', e); } }
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (flat.length ? (i + 1) % flat.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); select(flat[active]); }
    else if (e.key === 'Tab') { e.preventDefault(); setActive((i) => (flat.length ? (i + (e.shiftKey ? -1 : 1) + flat.length) % flat.length : 0)); }
  };

  const term = q.trim();
  let idx = -1;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(''); }}>
      <DialogContent
        className="top-[12%] translate-y-0 max-w-2xl gap-0 overflow-hidden p-0 [&>button:last-child]:hidden"
        data-testid="command-palette"
        onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current && inputRef.current.focus(); }}
      >
        <DialogTitle className="sr-only">Search everything</DialogTitle>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-700">
          {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" /> : <Search className="h-4 w-4 shrink-0 text-slate-400" />}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search candidates, resumes, clients, requirements, jobs, pages, settings…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            role="combobox"
            aria-expanded="true"
            aria-controls="dcs-palette-list"
            aria-activedescendant={flat[active] ? `dcs-palette-item-${active}` : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            data-testid="command-palette-input"
          />
          {q && (
            <button type="button" onClick={() => { setQ(''); inputRef.current && inputRef.current.focus(); }} className="rounded p-1 text-slate-400 hover:text-slate-600" aria-label="Clear">
              <X className="h-4 w-4" />
            </button>
          )}
          <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] text-slate-500 sm:inline dark:border-slate-700 dark:bg-slate-800">esc</kbd>
        </div>

        <div ref={listRef} id="dcs-palette-list" role="listbox" className="max-h-[60vh] overflow-y-auto py-1 scrollbar-thin">
          {flat.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              {loading ? 'Searching…' : term.length >= MIN_LIVE ? `Nothing found for "${term}".` : 'Type to search everything in the ATS.'}
            </div>
          )}
          {groups.map((g) => {
            const Icon = GROUP_ICON[g.key] || Boxes;
            return (
              <div key={g.key} className="px-2 pb-1" role="group" aria-label={g.label}>
                <div className="flex items-center justify-between px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <span className="flex items-center gap-1.5"><Icon className="h-3 w-3" />{g.label}</span>
                  {g.key === 'recent' && (
                    <button type="button" className="text-[10px] font-normal normal-case text-slate-400 hover:text-slate-600" onMouseDown={(e) => e.preventDefault()} onClick={() => { clearRecent(); setRecent([]); }}>
                      clear
                    </button>
                  )}
                  {g.more && (
                    <button type="button" className="text-[10px] font-normal normal-case text-blue-600 hover:underline" onMouseDown={(e) => e.preventDefault()} onClick={() => select({ id: `more:${g.key}`, label: `All ${g.label.toLowerCase()} for "${term}"`, to: g.more })}>
                      see all
                    </button>
                  )}
                </div>
                {g.items.map((item) => {
                  idx += 1;
                  const i = idx;
                  const isActive = i === active;
                  return (
                    <div
                      key={item.id}
                      id={`dcs-palette-item-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(item)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm',
                        isActive ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white' : 'text-slate-700 dark:text-slate-200',
                      )}
                      data-testid={`command-palette-item-${item.kind}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="truncate font-medium">{item.label}</span>
                          {item.subtitle && <span className="truncate text-xs text-slate-500 dark:text-slate-400">{item.subtitle}</span>}
                        </div>
                        {item.snippet && <Snippet s={item.snippet} />}
                        {!item.snippet && item.meta && <span className="block truncate text-xs text-slate-400">{item.meta}</span>}
                      </div>
                      {item.hint && <kbd className="rounded border border-slate-200 px-1 text-[10px] text-slate-500 dark:border-slate-700">{item.hint}</kbd>}
                      {isActive && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400 dark:border-slate-700">
          <span><kbd className="rounded border px-1">↑</kbd> <kbd className="rounded border px-1">↓</kbd> move · <kbd className="rounded border px-1">↵</kbd> open · <kbd className="rounded border px-1">esc</kbd> close</span>
          <span>open with <kbd className="rounded border px-1">/</kbd> or <kbd className="rounded border px-1">⌘K</kbd></span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
